/**
 * Overlay path for a nullable series on the price-chart scale (F12).
 *
 * X uses the FULL bar count so a warmup-null SMA still aligns with the
 * complete close path after decimation. Gaps break the path (new moveto)
 * rather than interpolating across a missing average (R2b).
 */
import { CHART_MAX_POINTS, CHART_PRECISION } from '../constants/chart';

import { chartXAt, chartYAt, sampleEvenlyIndices, type ChartRange } from './chart-path';

function overlaySampleIndices(count: number): readonly number[] {
  if (count <= CHART_MAX_POINTS) {
    return Array.from({ length: count }, (_unused, i) => i);
  }
  return sampleEvenlyIndices(count, CHART_MAX_POINTS);
}

function isDrawable(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isFinite(value);
}

/* eslint-disable functional/no-let, functional/no-loop-statements, functional/immutable-data */

export function overlayLinePath(
  values: readonly (number | null)[],
  range: ChartRange,
): string | null {
  const count = values.length;
  if (count < 2) return null;
  const parts: string[] = [];
  let drawing = false;
  for (const index of overlaySampleIndices(count)) {
    const value = values[index];
    if (!isDrawable(value)) {
      drawing = false;
      continue;
    }
    const x = chartXAt(index, count).toFixed(CHART_PRECISION);
    const y = chartYAt(value, range).toFixed(CHART_PRECISION);
    parts.push(`${drawing ? 'L' : 'M'}${x} ${y}`);
    drawing = true;
  }
  return parts.some((part) => part.startsWith('L')) ? parts.join('') : null;
}

/* eslint-enable functional/no-let, functional/no-loop-statements, functional/immutable-data */

/** Reject a length mismatch rather than stretching the overlay across the plot. */
export function alignedOverlayPath(
  values: readonly (number | null)[],
  barCount: number,
  range: ChartRange,
): string | null {
  if (values.length !== barCount) return null;
  return overlayLinePath(values, range);
}
